import React, { useState, useEffect, useCallback } from 'react'
import PaginatedTable from '../PaginatedTable'
import DateTime from '../DateTime'
import ModalDialog from '../ModalDialog'
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { getDownloadURL, ref } from 'firebase/storage'
import { auth, db, storage } from '../../firebase'
import Spinner from '../Spinner'
import { Download } from 'lucide-react'

function capitalize(s) {
  return s[0].toUpperCase() + s.slice(1)
}

export default function () {
  const [downloadError, setDownloadError] = useState(null)

  // Download archive from Firebase Storage
  const downloadArchive = useCallback(async (id) => {
    try {
      const jobRef = doc(db, 'dataExportJobs', id.toString())
      const jobSnap = await getDoc(jobRef)

      if (!jobSnap.exists()) throw new Error('Job not found')

      const { storagePath } = jobSnap.data()
      if (!storagePath) throw new Error('No archive path found')

      const downloadUrl = await getDownloadURL(ref(storage, storagePath))
      window.open(downloadUrl, '_blank')
    } catch (err) {
      setDownloadError(err)
    }
  }, [])

  // Download GPG Key from Firestore
  const downloadGpgKey = useCallback(async (keyFingerprint) => {
    try {
      const keyDoc = await getDoc(doc(db, 'gpgKeys', keyFingerprint))
      if (!keyDoc.exists()) throw new Error('GPG Key not found')

      const { armoredKey } = keyDoc.data()
      const url = URL.createObjectURL(new Blob([armoredKey], { type: 'text/plain' }))
      let a = document.createElement('a')
      a.href = url
      a.download = `livealarm_${keyFingerprint.slice(0, 8).toUpperCase()}.asc`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (err) {
      setDownloadError(err)
    }
  }, [])

  // 🔽 Pagination state (same style as incidents component)
  const [rows, setRows] = useState([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [opts, setOpts] = useState({
    itemsPerPage: 10,
    sortBy: 'id',
    sortOrder: 'desc',
    persistent: true,
    uid: 'data-export-jobs-list'
  })

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true)
      try {
        // Own jobs only. There is no organisation field on these documents,
        // so the rule keys on createdBy/userId -- and a list query has to
        // CONSTRAIN on it, because Firestore proves a list from the query, not
        // from the documents returned. An unfiltered list is refused outright.
        const uid = auth.currentUser?.uid
        if (!uid) {
          setRows([])
          setTotalCount(0)
          return
        }
        // One equality, no orderBy, and sorting/paging in memory. An
        // equality + orderBy needs a composite index per sortable column PER
        // DIRECTION -- ten of them for this table -- and a person has a
        // handful of export jobs, so paging them server-side buys nothing.
        const snap = await getDocs(
          query(collection(db, 'dataExportJobs'), where('createdBy', '==', uid))
        )
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))

        const dir = opts.sortOrder === 'desc' ? -1 : 1
        all.sort((a, b) => {
          const x = a[opts.sortBy], y = b[opts.sortBy]
          if (x === y) return 0
          if (x === undefined || x === null) return 1
          if (y === undefined || y === null) return -1
          return (x > y ? 1 : -1) * dir
        })

        const start = currentPage * opts.itemsPerPage
        setRows(all.slice(start, start + opts.itemsPerPage))
        setTotalCount(all.length)
      } catch (err) {
        console.error('Error fetching jobs:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchJobs()
  }, [currentPage, opts])

  const setPage = (page) => setCurrentPage(page)
  const setOptions = (newOpts) => setOpts(prev => ({ ...prev, ...newOpts }))

  const columns = [
    {
      name: 'ID',
      sortKey: 'id',
      render: (item) => item.id
    },
    {
      name: 'Status',
      sortKey: 'status',
      render: (item) => capitalize(item.status)
    },
    {
      name: 'Incident',
      sortKey: 'incidentId',
      render: (item) => (item.incidentId ? `#${item.incidentId}` : 'ALL')
    },
    {
      name: 'Created At',
      sortKey: 'createdAt',
      render: (item) => <DateTime value={item.createdAt} />
    },
    {
      name: 'Completed At',
      sortKey: 'updatedAt',
      render: (item) =>
        (item.status === 'ready' || item.status === 'downloaded') ? <DateTime value={item.updatedAt} /> : '-'
    },
    {
      name: 'Download',
      render: (item) => (
        item.url ? (
          <button
            className="button is-text"
            onClick={() => window.open(item.url, '_blank')}
            title="Download"
          >
            <Download size={16} />
          </button>
        ) : '-'
      )
    }
    // {
    //   name: 'Download',
    //   render: (item) => (
    //     <>
    //       {item.status === 'ready' && (
    //         <button className="button is-text" onClick={() => downloadArchive(item.id)}>
    //           Archive
    //         </button>
    //       )}
    //       {(item.status === 'ready' || item.status === 'downloaded') && (
    //         <button className="button is-text" onClick={() => downloadGpgKey(item.keyFingerprint)}>
    //           GPG Key
    //         </button>
    //       )}
    //     </>
    //   )
    // }
  ]

  if (loading) return <Spinner />

  return (
    <div className="box">
      <ModalDialog active={downloadError !== null} onClose={() => setDownloadError(null)}>
        <p>Download failed, reason: {downloadError && downloadError.message}</p>
      </ModalDialog>

      <PaginatedTable
        className="is-fullwidth is-hoverable"
        columns={columns}
        rows={rows}
        currentPage={currentPage}
        totalCount={totalCount}
        loading={loading}
        setPage={setPage}
        opts={opts}
        setOpts={setOptions}
      />
    </div>
  )
}
